import { Grid, Menu, MenuItem, ListItemText, ListItemIcon, Typography, Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ArrowDropDown, Clear, PlaylistAddCheck, CloudDownload } from '@material-ui/icons';
import { Button, StandardDialog, Toolbar, ConfirmationMenuItem, FileUploadModal } from '@roc/ui';
import { observer } from 'mobx-react';
import { useRef, useState } from 'react';
import { ProtectiveAdvancesSearchBar } from '../protectiveAdvancesSearchBar';
import { CounterPartyExpensesTab } from '../../types/types';
import { useBaseStore } from '@roc/feature-app-core';
import { useRefundsDashboardStore } from '../../hooks/useRefundsDashboardStore';
import { MarkAsPaidModal } from '../payables/markAsPaidModal';
import { ToolbarInfoItem } from '../toolbarInfoItem';

const useStyles = makeStyles(theme => ({
  infoItemsBox: {
    marginRight: '40px',
    marginLeft: '25px',
    display: 'flex',
    alignItems: 'center',
    gap: '30px'
  },
  containerBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  }
}));


export const RefundsToolbar = observer(() => {
  const classes = useStyles();
  const [payableRefNumber, setPayableRefNumber] = useState<string>('');
  const [showMarkAsPaidModal, setShowMarkAsPaidModal] = useState(false);
  const { refundsDashboardStore } = useRefundsDashboardStore();
  const anchorRef = useRef(null);
  const [showBulkActionsMenu, setShowBulkActionsMenu] = useState(false);
  const { globalStore } = useBaseStore();
  const { tab, payablesApprovedStore, counterPartyExpenseGridStore } = refundsDashboardStore;
  const [openUploadModal, setOpenUploadModal] = useState(false);

  const closeMenu = () => {
    setShowBulkActionsMenu(false);
  };

  const closeMarkAsPaidModal = () => {
    setPayableRefNumber('');
    setShowMarkAsPaidModal(false);
  };

  const openMarkAsPaidModal = () => {
    if (refundsDashboardStore.recordsIds && refundsDashboardStore.recordsIds.length > 0) {
      setShowMarkAsPaidModal(true);
    } else {
      globalStore.notificationStore.showErrorNotification({
        message: 'Please select an item from the table.',
      });
    }

  }

  const handleMarkAsPaidConfirm = async () => {
    await payablesApprovedStore.markMultipleAsPaid(payableRefNumber);
    counterPartyExpenseGridStore.resetAndFetchGridData();
    closeMarkAsPaidModal();
  }

  const downloadRefundsReport = async () => {
    if (refundsDashboardStore.recordsIds && refundsDashboardStore.recordsIds.length > 0) {
      await refundsDashboardStore.downloadRefundsReport();
    } else {
      globalStore.notificationStore.showErrorNotification({
        message: 'Please select an item from the table.',
      });
    }
  }

  const uploadRefundsFile = async (files) => {
    if (!files[0]?.name.endsWith(".xlsx")) {
      globalStore.notificationStore.showErrorNotification({
        message: 'Unsupported file format.',
      });
    } else {
      await refundsDashboardStore.uploadRefundsFile(files[0]);
      counterPartyExpenseGridStore.resetAndFetchGridData();
    }
  }

  return (
    <Grid container>
      <Grid item xs={12}>
        <Toolbar
          left={
            <ProtectiveAdvancesSearchBar counterPartyExpenseStore={refundsDashboardStore} label={"Entity"} />
          }>

          {tab === CounterPartyExpensesTab.PENDING_REFUNDS ? (
            <Box className={classes.containerBox} >
              <Box className={classes.infoItemsBox}>
                <ToolbarInfoItem value={refundsDashboardStore.recordsIds.length} label={"Count"} />
                <ToolbarInfoItem value={`${'$'}${refundsDashboardStore.totalAmount}`} label={"Amount"} />
              </Box>

              <Button
                variant="contained"
                color="primary"
                testId="create-order-bpo"
                onClick={() => {
                  setOpenUploadModal(true);
                }}
                size="small"
                disabled={!refundsDashboardStore.entity}
              >
                Upload Refunds File
              </Button>

              <div ref={anchorRef} style={{ display: 'inline-block' }}>
                <Button
                  id="basic-button"
                  aria-controls={open ? 'basic-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? 'true' : undefined}
                  onClick={() => { setShowBulkActionsMenu(true) }}
                  testId="open_menu"
                  variant="contained"
                  size="small"
                  color="primary"
                  disabled={!refundsDashboardStore.entity}
                >
                  Bulk Actions <ArrowDropDown style={{ marginRight: '-6px' }} />
                </Button>
                <Menu
                  id="basic-menu"
                  anchorEl={anchorRef.current}
                  open={showBulkActionsMenu}
                  onClose={closeMenu}
                  keepMounted={true}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  getContentAnchorEl={null}
                >
                  <MenuItem onClick={openMarkAsPaidModal} disabled={payablesApprovedStore.recordsIds.length < 1} >
                    <ListItemIcon>
                      <PlaylistAddCheck fontSize="small" />
                    </ListItemIcon>
                    <ListItemText onClick={closeMenu} >Mark as Paid</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={downloadRefundsReport} disabled={refundsDashboardStore.recordsIds.length < 1}>
                    <ListItemIcon>
                      <CloudDownload fontSize="small" />
                    </ListItemIcon>
                    <ListItemText onClick={closeMenu}>Download Refuns Report</ListItemText>
                  </MenuItem>
                </Menu>
              </div>
            </Box>
          ) : (
            <Grid container spacing={2}>
              <Grid item>
                <div ref={anchorRef} style={{ display: 'inline-block' }}>
                  <Button
                    id="basic-button"
                    aria-controls={open ? 'basic-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                    onClick={() => refundsDashboardStore.exportServicingDashboardData()}
                    testId="open_menu"
                    variant="contained"
                    size="small"
                    color="primary"
                  >
                    Export as Excel
                  </Button>
                </div>
              </Grid>
            </Grid>
          )
          }

        </Toolbar>
      </Grid>
      <MarkAsPaidModal
        payableRefNumber={payableRefNumber}
        setPayableRefNumber={setPayableRefNumber}
        showMarkAsPaidModal={showMarkAsPaidModal}
        closeMarkAsPaidModal={closeMarkAsPaidModal}
        handleMarkAsPaidConfirm={handleMarkAsPaidConfirm}
      />
      <FileUploadModal
        open={openUploadModal}
        title={`Upload Document`}
        onClose={() => setOpenUploadModal(false)}
        onUpload={(files) => uploadRefundsFile(files)}
      />
    </Grid>
  );
});
